/**
 * xlsx.ts — plik Excela z eksportu widoku listy, po stronie przeglądarki.
 *
 * Dlaczego nie CSV: CSV to goły tekst — nie niesie obramowań, koloru nagłówka ani formatowania
 * komórek. Plik .xlsx ma: kolorowy nagłówek, cienkie obramowanie każdej komórki, zamrożony
 * wiersz nagłówka z filtrem, szerokości kolumn dopasowane do treści oraz formatowanie warunkowe
 * z ustawień listy (reguły `formattingRules` układu) — wyliczone tym samym silnikiem co tabela
 * na ekranie, więc plik wygląda jak lista.
 *
 * Wartości trafiają do arkusza jako TEKST (to, co użytkownik widzi na ekranie: etykiety,
 * formaty dat i kwot), dlatego tekst zaczynający się od `=`/`+`/`-`/`@` jest tylko tekstem, nie
 * formułą — osobna ochrona (apostrof) jak w CSV nie jest potrzebna.
 *
 * `exceljs` ładowany leniwie (dynamiczny import) — ciężka biblioteka nie wchodzi do głównej paczki.
 */

import type { CSSProperties } from 'react';
import type { FormattingRule } from '@saas/shared';
import type { Font } from 'exceljs';

import workflow365Mark from '@/assets/workflow365-mark.png';
import {
  evaluateFormattingRules,
  precomputeRuleContext,
} from '@/components/configurable-table/formatting-evaluator';

import type { ExportColumn } from './cell-text';

/** Kolor tła nagłówka (ARGB) i tekstu nagłówka — jedno miejsce do zmiany wyglądu pliku. */
const HEADER_FILL = 'FFD9E2F3';
const HEADER_FONT = 'FF1F3A5F';
/** Kolor cienkiego obramowania komórek. */
const BORDER_COLOR = 'FF595959';

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 60;
const SHEET_NAME_MAX = 31;

/** Rozmiar logo w wierszu tytułu (piksele) i wcięcie tekstu tytułu, żeby nie wchodził pod logo. */
const LOGO_SIZE = 40;
const HEADING_INDENT = 5;
/** Wysokość wiersza tytułu i pustego odstępu między tytułem a tabelą (punkty). */
const HEADING_ROW_HEIGHT = 48;
const SPACER_ROW_HEIGHT = 12;

/** Logo jako PNG w base64; brak pliku/sieci (np. w testach) → `undefined`, plik powstaje bez logo. */
async function loadLogo(): Promise<string | undefined> {
  try {
    const res = await fetch(workflow365Mark);
    if (!res.ok) return undefined;
    const bytes = new Uint8Array(await res.arrayBuffer());
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary);
  } catch {
    return undefined;
  }
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** `#RRGGBB` (jak w układzie listy) → ARGB Excela; inny zapis → `undefined`. */
function toArgb(color: unknown): string | undefined {
  if (typeof color !== 'string') return undefined;
  const m = /^#([0-9a-f]{6})$/i.exec(color.trim());
  return m?.[1] ? `FF${m[1].toUpperCase()}` : undefined;
}

/** Nazwa arkusza: bez znaków zakazanych przez Excela (`\ / ? * [ ] :`), do 31 znaków. */
function sheetName(name: string): string {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return (cleaned || 'Eksport').slice(0, SHEET_NAME_MAX);
}

/** Szerokość kolumny wg najdłuższej linii treści (w znakach), w granicach rozsądku. */
function columnWidth(header: string, texts: readonly string[]): number {
  let longest = header.length;
  for (const text of texts) {
    for (const line of text.split(/\r?\n/)) longest = Math.max(longest, line.length);
  }
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, longest + 2));
}

/** Styl komórki z reguł formatowania listy (wiersz pod komórką: komórka wygrywa). */
function ruleStyle(cell: CSSProperties, row: CSSProperties): CSSProperties {
  return { ...row, ...cell };
}

export interface BuildXlsxArgs<TRow> {
  columns: readonly ExportColumn<TRow>[];
  rows: readonly TRow[];
  /** Nazwa arkusza (np. podstawa nazwy pliku). */
  title: string;
  /** Reguły formatowania warunkowego z układu listy; brak → sam nagłówek i obramowania. */
  formattingRules?: readonly FormattingRule[];
  /** Tytuł nad tabelą (np. „Kontrahenci — pobrano 21.10.2026, 11:50”); brak → bez wiersza tytułu. */
  heading?: string;
}

/** Zbudowany plik .xlsx jako Blob (do pobrania w przeglądarce). */
export async function buildXlsxBlob<TRow>(args: BuildXlsxArgs<TRow>): Promise<Blob> {
  const { columns, rows, title, formattingRules = [], heading } = args;
  // Wiersz nagłówków kolumn: pierwszy albo drugi (pod tytułem).
  const headerRowNumber = heading ? 3 : 1;
  const ExcelJS = (await import('exceljs')).default;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName(title), {
    views: [{ state: 'frozen', ySplit: headerRowNumber }],
  });

  const thin = { style: 'thin' as const, color: { argb: BORDER_COLOR } };
  const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

  // Tekst komórek liczymy raz — używany i do wpisania, i do szerokości kolumn.
  const texts = rows.map((row) => columns.map((c) => c.text(row)));

  sheet.columns = columns.map((c, i) => ({
    key: c.id,
    width: columnWidth(
      c.header,
      texts.map((r) => r[i] ?? ''),
    ),
  }));

  if (heading) {
    const headingRow = sheet.addRow([heading]);
    sheet.mergeCells(1, 1, 1, Math.max(columns.length, 1));
    headingRow.height = HEADING_ROW_HEIGHT;
    const cell = headingRow.getCell(1);
    cell.font = { bold: true, size: 16, color: { argb: HEADER_FONT } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: HEADING_INDENT };
    const logo = await loadLogo();
    if (logo) {
      const imageId = workbook.addImage({ base64: logo, extension: 'png' });
      sheet.addImage(imageId, {
        tl: { col: 0.1, row: 0.145 },
        ext: { width: LOGO_SIZE, height: LOGO_SIZE },
      });
    }
    // Pusty wiersz-odstęp między tytułem a tabelą.
    sheet.addRow([]).height = SPACER_ROW_HEIGHT;
  }

  const headerRow = sheet.addRow(columns.map((c) => c.header));
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    cell.border = allBorders;
  });

  const rules = [...formattingRules];
  const valueByColumn = new Map(columns.map((c) => [c.id, c.value]));
  const getValue = (row: TRow, columnId: string): unknown => valueByColumn.get(columnId)?.(row);
  const allRows = [...rows];
  const context = rules.length > 0 ? precomputeRuleContext(allRows, rules, getValue) : undefined;

  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.addRow(texts[rowIndex]);
    columns.forEach((column, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.alignment = { vertical: 'top', wrapText: true };
      cell.border = allBorders;
      if (!context) return;
      const result = evaluateFormattingRules({
        row,
        rowIndex,
        columnId: column.id,
        rules,
        context,
        getValue,
        allRows,
      });
      const css = ruleStyle(result.cellStyle, result.rowStyle);
      const background = toArgb(css.backgroundColor);
      if (background) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: background } };
      }
      const font: Partial<Font> = {};
      const fontColor = toArgb(css.color);
      if (fontColor) font.color = { argb: fontColor };
      if (css.fontWeight === 'bold') font.bold = true;
      if (css.fontStyle === 'italic') font.italic = true;
      if (Object.keys(font).length > 0) cell.font = font;
      const borderColor = toArgb(css.borderColor);
      if (borderColor) {
        const heavy = parseInt(String(css.borderWidth ?? '1'), 10) >= 3;
        const side = {
          style: heavy ? ('medium' as const) : ('thin' as const),
          color: { argb: borderColor },
        };
        cell.border = { top: side, left: side, bottom: side, right: side };
      }
    });
  });

  sheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: Math.max(columns.length, 1) },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}
